import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Bell, Tag, ExternalLink, Clock } from "lucide-react";
import { KalkiLogo } from "@/components/KalkiLogo";
import { useAuth } from "@/context/AuthContext";
import { getTz, setTz } from "@/lib/format";
import { cn } from "@/lib/utils";

function Badge({ badge }) {
  if (!badge) return null;
  const gold = badge.tone === "gold";
  return (
    <span className={cn("ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full",
      gold ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-[#262626] text-[#A3A3A3]")}>
      {badge.label}
    </span>
  );
}

export function AppShell({ nav, role, notifications = 0 }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/login"); };
  const initials = (user?.name || "K").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isAdmin = role === "ADMIN";
  const [tz, setTzState] = React.useState(getTz());
  const toggleTz = () => { const n = tz === "UTC" ? "Local" : "UTC"; setTz(n); setTzState(n); };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] relative z-[1]">
      <aside className="w-64 flex-shrink-0 border-r border-[#262626] bg-[#0D0D0D] flex flex-col fixed h-full">
        <div className="px-5 py-5 border-b border-[#262626]"><KalkiLogo /></div>

        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {nav.map((group, gi) => (
            <div key={gi}>
              {group.group && (
                <div className="px-3 mb-1.5 mt-1 text-[10px] uppercase tracking-[0.16em] text-[#525252] font-semibold">{group.group}</div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.replace(/\//g, "-").replace(/^-/, "")}`}
                    className={({ isActive }) =>
                      cn("group flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors relative",
                        isActive ? "text-[#D4AF37] bg-[rgba(212,175,55,0.06)]" : "text-[#A3A3A3] hover:text-[#F5F5F5] hover:bg-[#1A1A1A]")}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-[#D4AF37] rounded-r" />}
                        <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                        <span className="font-medium truncate">{item.label}</span>
                        <Badge badge={item.badge} />
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-[#262626] space-y-2">
          {isAdmin && (
            <a href="https://kalkiai.trade" target="_blank" rel="noreferrer" data-testid="white-label-card"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-sm border border-[#262626] bg-[#141414] hover:border-[#D4AF37] transition-colors group">
              <Tag className="w-4 h-4 text-[#D4AF37]" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-[#F5F5F5] truncate">Kalkiai.trade</div>
                <div className="text-[10px] text-[#737373]">Open White Label</div>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-[#525252] group-hover:text-[#D4AF37]" />
            </a>
          )}
          <div className="flex items-center gap-3 px-2 py-1.5">
            <div className="w-9 h-9 rounded-sm bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.2)] flex items-center justify-center text-[#D4AF37] text-xs font-bold font-mono">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#F5F5F5] font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-[#737373] truncate">{user?.organization}</div>
            </div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-[#A3A3A3] hover:text-[#EF4444] hover:bg-[rgba(239,68,68,0.08)] transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 h-14 border-b border-[#262626] bg-[rgba(10,10,10,0.8)] backdrop-blur-md flex items-center justify-end gap-4 px-6 lg:px-10">
          <button data-testid="notifications-btn" className="relative text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
            <Bell className="w-5 h-5" />
            {notifications > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold rounded-full bg-[#EF4444] text-white">
                {notifications > 99 ? "99+" : notifications}
              </span>
            )}
          </button>
          <button onClick={toggleTz} data-testid="tz-toggle" title="Toggle timezone (UTC / Local)"
            className="flex items-center gap-1.5 text-xs font-medium text-[#A3A3A3] hover:text-[#D4AF37] border border-[#262626] rounded-sm px-2.5 py-1.5 transition-colors">
            <Clock className="w-3.5 h-3.5" /> {tz}
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.2)] flex items-center justify-center text-[#D4AF37] text-[11px] font-bold font-mono">{initials}</div>
            <div className="hidden sm:block leading-tight">
              <div className="text-sm text-[#F5F5F5] font-medium">{user?.organization}</div>
            </div>
          </div>
        </header>

        <div className="flex-1 max-w-[1500px] w-full mx-auto px-6 lg:px-10 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
